<script setup lang="ts">
import PrimeDialog from 'primevue/dialog'

interface Props {
  modelValue: boolean
  header?: string
  /** Panel width (any CSS length). Default `'400px'`. */
  width?: string
  /** Tap on mask dismisses the dialog. Default `true`. */
  dismissable?: boolean
  /** Show the X close icon in the header. Default `true`. */
  showCloseIcon?: boolean
  /** Modal (with mask + focus trap). Default `true`. */
  modal?: boolean
  /**
   * Keep PrimeVue's default header/content padding. Pass `false` when the
   * caller renders its own chrome (own header row, dividers, sticky footer)
   * and needs the panel edge-to-edge. Default `true`.
   */
  padded?: boolean
  /**
   * Hide the built-in header and drop content padding so the slot owns the
   * whole panel (custom header / body / footer). Default `false`.
   */
  flush?: boolean
  /**
   * Gap above the panel (any CSS length). For top-anchored dialogs
   * (`position="top"`), where PrimeVue's own 0.75rem margin sits the panel
   * hard against the viewport edge.
   */
  topOffset?: string
}

const props = withDefaults(defineProps<Props>(), {
  width: '400px',
  dismissable: true,
  showCloseIcon: true,
  modal: true,
  padded: true,
  flush: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'hide'): void
}>()

// Either opt-out (`padded=false` or `flush`) zeroes the PrimeVue padding.
const edgeToEdgePadding = props.padded && !props.flush ? undefined : { padding: '0' }

const pt = {
  root: {
    class: 'app-dialog__panel',
    style: {
      width: props.width,
      maxWidth: 'calc(100vw - 2rem)',
      ...(props.topOffset && {
        marginTop: props.topOffset,
        maxHeight: `calc(100% - ${props.topOffset} - 0.75rem)`,
      }),
      ...(props.flush && { borderRadius: 'var(--p-border-radius-xl)', overflow: 'hidden' }),
    },
  },
  header: { class: 'app-dialog__header', style: edgeToEdgePadding },
  content: { class: 'app-dialog__content', style: edgeToEdgePadding },
  mask: { class: 'app-dialog__mask' },
}

const onHide = () => {
  emit('update:modelValue', false)
  emit('hide')
}
</script>

<template>
  <PrimeDialog
    :visible="modelValue"
    :header="header"
    :dismissable-mask="dismissable"
    :closable="showCloseIcon"
    :modal="modal"
    :show-header="!flush"
    :pt="pt"
    :block-scroll="true"
    @update:visible="(v: boolean) => !v && onHide()"
  >
    <template v-if="$slots.header" #header>
      <slot name="header" />
    </template>
    <slot />
  </PrimeDialog>
</template>
